<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_SAVINGS_ACCRUAL_HISTORY_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport">
              {{ $t('PDF') }}
            </b-dropdown-item>
          </b-dropdown></b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="savingsAccrualHistoryTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="SN"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(financialDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDateYear(data.value) : '' }}
        </span>
      </template>
      <template #cell(amount)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(dateCreated)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDateYear(data.value) : '' }}
        </span>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="savingsAccrualHistoryRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.AccountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter Account Name')"
                type="text"
                for="account-name"
              />
            </b-col>
            <!-- Account Name -->
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.AccountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="text"
                for="account-number"
              />
            </b-col>
          <!-- Account Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->

            <b-col md="6 mt-2">
              <SwitchButton
                v-model="filter.useCurrentFinancialDate"
                :label="$t('Use Current Financial Date')"
                name="use-current-financial-date"
                :readonly="isReadonly"
                @input="setUseCurrentFinancialDate(filter.useCurrentFinancialDate)"
              />
            </b-col>
          </b-row>
          <b-row class="match-height">
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                rules="required"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :error-name="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
                :disabled="isDisabled"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                rules="required"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :error-name="$t('Financial Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
                :disabled="isDisabled"
              />
            </b-col>
          <!-- End Date -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { toBalance, formatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import toNumber from '../../../../helpers/toNumber'
import { customDate, toCustomDate } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SelectField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    ValidationObserver,
    DatePickerField,
    SwitchButton,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      formatAmount,
      toBalance,
      isDisabled: false,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Savings Accrual History Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'product', sortable: true, label: 'Product' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
      ],
      filter: {
        accountNumber: '',
        accountName: '',
        financialDateFrom: null,
        financialDateTo: null,
        branchCode: '',
        useCurrentFinancialDate: true,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Financial Date': item.financialDate ? this.formatCustomDate(item.financialDate) : '',
        'Account Number': item.accountNumber,
        'Reference Number': item.referenceNumber,
        Branch: item.branch,
        Product: item.product,
        Amount: item.amount > 0 ? formatAmount(item.amount) : '',
        'Date Created': item.dateCreated ? this.formatCustomDate(item.dateCreated) : '',
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchSavingsAccrualHistoryReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchSavingsAccrualHistoryReport()
    },
  },
  mounted() {
    this.fetchSavingsAccrualHistoryReport()
  },
  methods: {
    ...mapActions({
      getProduct: 'account/getProducts',
      getBranchByCode: 'operations/getBranchByCode',
      getSavingsAccrualHistory: 'reports/getSavingsAccrualHistory',
      downloadSavingsAccrualReport: 'reports/downloadSavingsAccrualReport',
    }),

    resetFilters() {
      this.filter = { ...null }
      this.filter.financialDateFrom = null
      this.filter.financialDateTo = null
      this.filter.useCurrentFinancialDate = true
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDateFrom = null
      this.filter.financialDateTo = null
      this.filter.useCurrentFinancialDate = true
      this.fetchSavingsAccrualHistoryReport()
    },

    setUseCurrentFinancialDate(dataValue) {
      if (dataValue === false) {
        this.isDisabled = false
      } else {
        this.isDisabled = true
        this.filter.financialDateFrom = null
        this.filter.financialDateTo = null
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      if (this.filter.useCurrentFinancialDate === false) {
        bvModalEvt.preventDefault()
        this.$refs.savingsAccrualHistoryRules.validate().then(async success => {
          if (success) {
            this.fetchSavingsAccrualHistoryReport()
          }
        })
      } else {
        this.fetchSavingsAccrualHistoryReport()
      }
    },
    async fetchSavingsAccrualHistoryReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getSavingsAccrualHistory({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadSavingsAccrualReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: reportPermissions.EXPORT_SAVINGS_ACCRUAL_HISTORY_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadSavingsAccrualReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: reportPermissions.EXPORT_SAVINGS_ACCRUAL_HISTORY_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async pdfExport() {
      try {
        this.isBusy = true
        const response = await this.downloadSavingsAccrualReport({
          search: this.filter,
          fileExportFormat: 2,
          permission: reportPermissions.EXPORT_SAVINGS_ACCRUAL_HISTORY_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
    formatCustomDateYear: customDate,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
